import type { Query, QueryClient } from '@tanstack/react-query';
import { mutationOptions, queryOptions } from '@tanstack/react-query';
import type { SkinAnalysisResultStatus } from '../../../model/skinAnalysis/skinAnalysis';
import {
  isSkinAnalysisRoutineTerminal,
  SkinAnalysisRecommendationProductGoneError,
  SkinAnalysisRecommendationsNotReadyError,
} from '../../../model/skinAnalysis/skinAnalysis';
import { skinAnalysisAPIClient } from '../SkinAnalysisAPIClient';
import { LOYALTY_QUERY_KEYS } from '../../loyalty/utils/keys';
import { SKIN_ANALYSIS_QUERY_KEYS } from './keys';
import { classifySkinAnalysisPollFailure, isSkinAnalysisPollStopped } from './pollFailure';
import type {
  CreateSkinAnalysisHautRunRequest,
  CreateSkinAnalysisHautRunResponse,
  CreateSkinAnalysisImageUploadUrlRequest,
  CreateSkinAnalysisImageUploadUrlResponse,
  CreateSkinAnalysisResultRequest,
  CreateSkinAnalysisResultResponse,
  GetActiveSkinAnalysisSurveyRequest,
  GetSkinAnalysisRecommendationProductDetailRequest,
  GetSkinAnalysisRecommendationProductsRequest,
  GetSkinAnalysisRecommendationsPathParams,
  GetSkinAnalysisReportPathParams,
  GetSkinAnalysisResultStatusPathParams,
  GetSkinAnalysisResultStatusResponse,
  PutSkinAnalysisLocationRequest,
  PutSkinAnalysisLocationResponse,
  SearchSkinAnalysisLocationsRequest,
  SearchSkinAnalysisLocationsResponse,
  SubmitSkinAnalysisSurveyResponseRequest,
  SubmitSkinAnalysisSurveyResponseResponse,
} from './types';

/** Status-poll cadence. The new contract provides no server interval, so the app owns it. */
const SKIN_ANALYSIS_POLLING_INTERVAL_MS = 3000;
/** Retries per failed poll request. Bounded: the interval keeps polling anyway, so this only
 *  covers a blip within one tick rather than standing in for the poll itself. */
const SKIN_ANALYSIS_POLL_RETRY_LIMIT = 2;
/** Retries for me/status — the same budget the app and web query clients give every query. */
const SKIN_ANALYSIS_STATUS_RETRY_LIMIT = 3;
/** Retries for the routine read — the same budget as every other query, kept for everything but a `failed` verdict. */
const SKIN_ANALYSIS_RECOMMENDATIONS_RETRY_LIMIT = 3;
/** How long the Analysing screen waits on the status poll before failing (3 min). */
export const SKIN_ANALYSIS_POLLING_TIMEOUT_MS = 180000;

/** Polling stops on these. Quality failure is NOT a poll status — it is returned
 *  synchronously by POST /results (contract API 6), so retake never arrives here. */
const SKIN_ANALYSIS_TERMINAL_STATUSES = new Set<SkinAnalysisResultStatus>(['completed', 'failed', 'expired']);

const isTerminalStatus = (status: SkinAnalysisResultStatus | undefined): boolean =>
  status !== undefined && SKIN_ANALYSIS_TERMINAL_STATUSES.has(status);

/** A live-product failure that is a verdict, not a blip — retrying it only delays the screen. */
const isSettledRecommendationProductFailure = (error: unknown): boolean =>
  error instanceof SkinAnalysisRecommendationsNotReadyError ||
  error instanceof SkinAnalysisRecommendationProductGoneError;

/**
 * Failure-tolerant variant of a query: any failure surfaces as `undefined` data instead of
 * reaching an error boundary, and nothing retries. For reads that decorate a screen rather
 * than gate it — the cost is that a broken endpoint looks identical to an empty one, so
 * monitor such routes server-side rather than expecting the screen to surface them.
 *
 * Generic over the concrete `queryOptions` result so the data-type branding on `queryKey`
 * survives — a plain `{ ...opts, retry: false }` literal at each call site kept the shape
 * but lost the type each time.
 */
const failureTolerant = <T extends { queryKey: readonly unknown[] }>(options: T) => ({
  ...options,
  retry: false,
  throwOnError: false,
});

export const SKIN_ANALYSIS_QUERIES = {
  getMyStatus: () =>
    queryOptions({
      queryKey: SKIN_ANALYSIS_QUERY_KEYS.getMyStatus(),
      queryFn: () => skinAnalysisAPIClient.getMyStatus(),
      // A 401 means the session the client still trusts was rejected — retrying only delays
      // the caller re-checking who the user is.
      retry: (failureCount: number, error: Error) =>
        classifySkinAnalysisPollFailure(error) !== 'unauthenticated' && failureCount < SKIN_ANALYSIS_STATUS_RETRY_LIMIT,
    }),

  /** Everything the Skin Diary entry page renders — precomputed server-side, one call. */
  getMyOverview: (userId?: string | number) =>
    queryOptions({
      // One key shape under the prefix everyone invalidates; cart callers pass the user to keep
      // a signed-in visitor's overview from surviving an account switch.
      queryKey: [...SKIN_ANALYSIS_QUERY_KEYS.getMyOverview(), { userId: userId ?? null }] as const,
      queryFn: () => skinAnalysisAPIClient.getMyOverview(),
    }),

  getActiveSurvey: (params: GetActiveSkinAnalysisSurveyRequest = {}) =>
    queryOptions({
      queryKey: SKIN_ANALYSIS_QUERY_KEYS.getActiveSurvey(params.currency),
      queryFn: ({ signal }) => skinAnalysisAPIClient.getActiveSurvey(params, signal),
    }),

  /** Failure-tolerant active-survey read: only needed by the submit path; a 404 must not block the survey. */
  getActiveSurveySafe: (params: GetActiveSkinAnalysisSurveyRequest = {}) =>
    failureTolerant(SKIN_ANALYSIS_QUERIES.getActiveSurvey(params)),

  /** The caller's submitted survey, read-only. Its `editable` is what any UI must gate on. */
  getMySurvey: () =>
    queryOptions({
      queryKey: SKIN_ANALYSIS_QUERY_KEYS.getMySurvey(),
      queryFn: () => skinAnalysisAPIClient.getMySurvey(),
    }),

  getLocation: () =>
    queryOptions({
      queryKey: SKIN_ANALYSIS_QUERY_KEYS.getLocation(),
      queryFn: () => skinAnalysisAPIClient.getLocation(),
    }),

  /** Failure-tolerant location read: only pre-fills the region card, so a 404/401 must not crash the screen. */
  getLocationSafe: () => failureTolerant(SKIN_ANALYSIS_QUERIES.getLocation()),

  getEnvironment: () =>
    queryOptions({
      queryKey: SKIN_ANALYSIS_QUERY_KEYS.getEnvironment(),
      queryFn: () => skinAnalysisAPIClient.getEnvironment(),
    }),

  /**
   * Failure-tolerant climate read. The expected failures are 503 (no measurement usable)
   * and 409 (no location resolves), which genuinely mean "nothing to show"; the rest are
   * swallowed too, deliberately — climate is decoration on a required funnel step, so a
   * 500 must not block the user from continuing.
   */
  getEnvironmentSafe: () => failureTolerant(SKIN_ANALYSIS_QUERIES.getEnvironment()),

  getResultStatus: (params: GetSkinAnalysisResultStatusPathParams) =>
    queryOptions({
      queryKey: SKIN_ANALYSIS_QUERY_KEYS.getResultStatus(params),
      queryFn: () => skinAnalysisAPIClient.getResultStatus(params),
      enabled: Boolean(params.resultId),
    }),

  /**
   * Polls until a terminal status. The timeout budget is enforced by the caller,
   * since React Query has no built-in poll timeout.
   *
   * Two things stop the interval: a terminal STATUS, and a failure no waiting can fix (a lost
   * session or a result the caller does not own). Everything else keeps polling — React Query
   * would otherwise happily re-request a 404 every three seconds until the caller's timeout,
   * then report it as "your analysis is taking longer than expected".
   */
  pollResultStatus: (params: GetSkinAnalysisResultStatusPathParams, queryClient: QueryClient) =>
    queryOptions({
      queryKey: SKIN_ANALYSIS_QUERY_KEYS.getResultStatus(params),
      queryFn: async () => {
        const result = await skinAnalysisAPIClient.getResultStatus(params);
        // The report can open before the full analysis completes. Refresh credits when
        // completion is confirmed, including runs resumed without a create mutation.
        if (result.status === 'completed') {
          void queryClient.invalidateQueries({ queryKey: LOYALTY_QUERY_KEYS.default() });
        }
        return result;
      },
      enabled: Boolean(params.resultId),
      // A 401/403/404 is the server's final answer — retrying it only delays telling the user.
      retry: (failureCount: number, error: Error) =>
        !isSkinAnalysisPollStopped(error) && failureCount < SKIN_ANALYSIS_POLL_RETRY_LIMIT,
      refetchInterval: (query: Query<GetSkinAnalysisResultStatusResponse>) => {
        if (isTerminalStatus(query.state.data?.status)) {
          return false;
        }
        if (isSkinAnalysisPollStopped(query.state.error)) {
          return false;
        }
        return SKIN_ANALYSIS_POLLING_INTERVAL_MS;
      },
    }),

  getReport: (params: GetSkinAnalysisReportPathParams) =>
    queryOptions({
      queryKey: SKIN_ANALYSIS_QUERY_KEYS.getReport(params),
      queryFn: () => skinAnalysisAPIClient.getReport(params),
      enabled: Boolean(params.resultId),
    }),

  getRecommendations: (params: GetSkinAnalysisRecommendationsPathParams) =>
    queryOptions({
      queryKey: SKIN_ANALYSIS_QUERY_KEYS.getRecommendations(params),
      queryFn: () => skinAnalysisAPIClient.getRecommendations(params),
      enabled: Boolean(params.resultId),
      // A section the server calls `failed` answers the same on every retry — retrying it only keeps
      // the reader off the terminal copy. Every other rejection keeps the default budget.
      retry: (failureCount: number, error: Error) =>
        !isSkinAnalysisRoutineTerminal(error) && failureCount < SKIN_ANALYSIS_RECOMMENDATIONS_RETRY_LIMIT,
    }),

  /**
   * Live product reads (SSD-6340). The server answers `Cache-Control: private, no-store` and
   * re-prices on every read, and the contract wants a fresh list on every entry and a price that
   * is the DETAIL's own, never the list's — so nothing here counts as fresh and every mount
   * refetches. A 503 keeps its retries (the catalog/FX read may well recover); the typed 409 and
   * 404 stop them, because no retry changes either verdict.
   */
  getRecommendationProducts: (params: GetSkinAnalysisRecommendationProductsRequest) =>
    queryOptions({
      queryKey: SKIN_ANALYSIS_QUERY_KEYS.getRecommendationProducts(params),
      queryFn: () => skinAnalysisAPIClient.getRecommendationProducts(params),
      enabled: Boolean(params.resultId),
      staleTime: 0,
      refetchOnMount: 'always',
      retry: (failureCount: number, error: Error) =>
        !isSettledRecommendationProductFailure(error) && failureCount < SKIN_ANALYSIS_POLL_RETRY_LIMIT,
    }),

  getRecommendationProductDetail: (params: GetSkinAnalysisRecommendationProductDetailRequest) =>
    queryOptions({
      queryKey: SKIN_ANALYSIS_QUERY_KEYS.getRecommendationProductDetail(params),
      queryFn: () => skinAnalysisAPIClient.getRecommendationProductDetail(params),
      enabled: Boolean(params.resultId) && Boolean(params.recommendationItemId),
      staleTime: 0,
      refetchOnMount: 'always',
      retry: (failureCount: number, error: Error) =>
        !isSettledRecommendationProductFailure(error) && failureCount < SKIN_ANALYSIS_POLL_RETRY_LIMIT,
    }),

  /** Submitting flips me/status survey gating and changes the About My Skin card on the overview. */
  submitSurveyResponse: (queryClient: QueryClient) =>
    mutationOptions<SubmitSkinAnalysisSurveyResponseResponse, unknown, SubmitSkinAnalysisSurveyResponseRequest>({
      mutationKey: SKIN_ANALYSIS_QUERY_KEYS.submitSurveyResponse(),
      mutationFn: (payload) => skinAnalysisAPIClient.submitSurveyResponse(payload),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: SKIN_ANALYSIS_QUERY_KEYS.getMyStatus() });
        void queryClient.invalidateQueries({ queryKey: SKIN_ANALYSIS_QUERY_KEYS.getMyOverview() });
      },
    }),

  putLocation: (queryClient: QueryClient) =>
    mutationOptions<PutSkinAnalysisLocationResponse, unknown, PutSkinAnalysisLocationRequest>({
      mutationKey: SKIN_ANALYSIS_QUERY_KEYS.putLocation(),
      mutationFn: (payload) => skinAnalysisAPIClient.putLocation(payload),
      // Async on purpose: React Query waits on what onSuccess returns, so the mutation
      // resolving means "the new city's climate has landed", not merely "the PUT returned".
      // That is what lets a caller hold its sheet open until there is something to show.
      onSuccess: async (data) => {
        // The PUT echoes the saved preference, so write it through — an invalidation here
        // would refetch the same bytes the server just returned.
        queryClient.setQueryData(SKIN_ANALYSIS_QUERIES.getLocation().queryKey, data);
        // Climate is measured AT the location, so the previous city's reading is not stale
        // data to revalidate — it is the wrong answer. `reset` rather than `invalidate`:
        // invalidation keeps serving the old numbers until the refetch lands, which paints
        // them under the new city's name, and `getEnvironmentSafe` neither retries nor
        // throws, so a failed refetch would strand them there for good. Resetting clears
        // the entry first, so the cards fall to their no-data state and can only fill back
        // in with a reading actually taken at the new location.
        await queryClient.resetQueries({ queryKey: SKIN_ANALYSIS_QUERY_KEYS.getEnvironment() });
      },
    }),

  createImageUploadUrl: () =>
    mutationOptions<CreateSkinAnalysisImageUploadUrlResponse, unknown, CreateSkinAnalysisImageUploadUrlRequest>({
      mutationKey: SKIN_ANALYSIS_QUERY_KEYS.createImageUploadUrl(),
      mutationFn: (payload) => skinAnalysisAPIClient.createImageUploadUrl(payload),
    }),

  /** Starts the survey-independent image run right after the scan (SSD-6097). */
  createHautRun: () =>
    mutationOptions<CreateSkinAnalysisHautRunResponse, unknown, CreateSkinAnalysisHautRunRequest>({
      mutationKey: SKIN_ANALYSIS_QUERY_KEYS.createHautRun(),
      mutationFn: (payload) => skinAnalysisAPIClient.createHautRun(payload),
    }),

  searchLocations: () =>
    mutationOptions<SearchSkinAnalysisLocationsResponse, unknown, SearchSkinAnalysisLocationsRequest>({
      mutationKey: SKIN_ANALYSIS_QUERY_KEYS.searchLocations(),
      mutationFn: (params) => skinAnalysisAPIClient.searchLocations(params),
    }),

  /** A new active analysis spends scan quota, so both me/status and the entry overview go stale. */
  createResult: (queryClient: QueryClient) =>
    mutationOptions<CreateSkinAnalysisResultResponse, unknown, CreateSkinAnalysisResultRequest>({
      mutationKey: SKIN_ANALYSIS_QUERY_KEYS.createResult(),
      mutationFn: (payload) => skinAnalysisAPIClient.createResult(payload),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: SKIN_ANALYSIS_QUERY_KEYS.getMyStatus() });
        void queryClient.invalidateQueries({ queryKey: SKIN_ANALYSIS_QUERY_KEYS.getMyOverview() });
      },
    }),
};
